import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { MultiDepositTabState } from './MultiDepositTab.state';
import { DepositTabWrapper } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/DepositProcedure.style';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { useCommon } from 'src/domains/common/Common';
import { PaymentAdditions } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/depositSignupPage/PaymentsAdditions';
import { mapErrorsMessages } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/errorMessages/DepositErrorMessages';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import {
    getPaymentMethodById,
    renderPaymentIcon,
    usePaymentCardLogos,
    usePaymentMethodsConfig,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/multiDepositJourney/PaymentMethodsConfig/paymentMethodsConfig';
import {
    PaymentMethodLabel,
    PaymentLogosContainer,
    PaymentMethodIcon,
    PaymentMethodText,
    ArrowIcon,
    PaymentMethodSection,
    PayButton,
    PayButtonApplePay,
    CashBalanceWrapper,
    AmountLabel,
    InputAmount,
} from './MultiDepositTab.style';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { RingFencedFundsMessageUpdated } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/ringFencedFundsWrapper/RingFencedFundsWrapperUpdated';
import { RingFencedFundsMessage } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/ringFencedFundsWrapper/RingFencedFundsWrapper';
import { KycWarning } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/kycWarning/KycWarning';
import { ButtonApplePayIcon } from 'src/domains/common/paymentMethods/paymentButtonIcon/ButtonApplePayIcon';
import { PaymentMethodButton } from 'src/ui/components/list-item/actionable/payment-method/PaymentMethod';
import { RemainingLimitNew } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/remainingLimit/RemainingLimitNew';

interface MultiDepositTabPropsType {
    state: MultiDepositTabState;
    isSignup: boolean;
}

export const MultiDepositTab = observer(
    'MultiDepositTab',
    ({ state, isSignup }: MultiDepositTabPropsType): JSX.Element => {
        const common = useCommon();
        const {
            config: { rollingNetDepositLimit },
        } = ConfigComponents.get(common);
        const basicDataModel = BasicDataModel.get(common);
        const languagesState = LanguagesState.get(common);
        const paymentMethods = usePaymentMethodsConfig();
        const paymentCardLogos = usePaymentCardLogos();
        const {
            config: { isSaferGambling, showKycStatus },
        } = ConfigComponents.get(common);
        const selectedMethod = getPaymentMethodById(state.selectedPaymentMethod.value, paymentMethods);
        const shouldDisplayRemainingLimit =
            rollingNetDepositLimit === false || state.remainingLimitState.getRemainingLimit === undefined;
        return (
            <DepositTabWrapper isSignup={isSignup}>
                <AmountLabel data-test='amount-label'>
                    <I18n
                        langKey='account.multi-deposit.input-label'
                        defaultText='Amount'
                    />
                </AmountLabel>
                <InputAmount
                    type='text'
                    maxLength={10}
                    placeholder='0.00'
                    currency={basicDataModel.moneySymbol}
                    state={state.depositAmount}
                    onChange={state.onChange}
                    onBlur={state.setAmount}
                    colorTheme={isSignup ? 'dark' : 'light'}
                    inputHasBackground={false}
                    dataTest='multi-deposit-amount-input'
                />

                <PaymentAdditions
                    key='payment-additions'
                    currency={basicDataModel.moneySymbol}
                    value={state.depositAmount.value}
                    onChange={state.handleAdditionsChange}
                />

                <CashBalanceWrapper data-test='cash-balance'>
                    <I18n
                        langKey='account.top-up.cash-balance-label'
                        defaultText='Cash balance'
                    />
                    <span>{state.showBalance}</span>
                </CashBalanceWrapper>

                {shouldDisplayRemainingLimit ? null : (
                    <RemainingLimitNew
                        value={state.remainingLimitState.getRemainingLimit.format(basicDataModel.currency)}
                        isSignup={isSignup}
                    />
                )}

                {state.errorMessagesState.errors.length > 0
                    ? state.errorMessagesState.errors.map((error) => mapErrorsMessages(error, languagesState))
                    : null}
                <PaymentMethodSection>
                    <PaymentMethodLabel data-test='payment-method-label'>
                        <I18n
                            langKey='account.top-up.payment-method'
                            defaultText='Payment Method'
                        />
                        <PaymentLogosContainer>{paymentCardLogos}</PaymentLogosContainer>
                    </PaymentMethodLabel>

                    <PaymentMethodButton
                        onClick={state.redirectToSelectPaymentMethod}
                        isSignup={isSignup}
                        dataTest='multi-deposit-select-method'
                    >
                        <PaymentMethodIcon>
                            {selectedMethod !== undefined && renderPaymentIcon(selectedMethod, isSignup)}
                        </PaymentMethodIcon>
                        <PaymentMethodText>{selectedMethod?.title ?? 'Select Payment Method'}</PaymentMethodText>
                        <ArrowIcon
                            position='right'
                            isSignup={isSignup}
                        />
                    </PaymentMethodButton>
                </PaymentMethodSection>

                {state.hasRingFencedFunds === false ? (
                    isSaferGambling.available ? (
                        <RingFencedFundsMessageUpdated isSignup={state.isSignup} />
                    ) : (
                        <RingFencedFundsMessage
                            isMoreShown={state.showMoreRingFenceFlag}
                            toggleShowMore={state.toggleShowMore}
                            isSignup={state.isSignup}
                        />
                    )
                ) : null}

                {selectedMethod?.id === 'apple-pay' ? (
                    <PayButtonApplePay
                        size='large'
                        disabled={state.isButtonDisabled}
                        onClick={state.submitDepositForm}
                        type='submit'
                        dataTest='multi-deposit-submit'
                    >
                        <ButtonApplePayIcon />
                    </PayButtonApplePay>
                ) : (
                    <PayButton
                        size='large'
                        disabled={state.isButtonDisabled}
                        onClick={state.submitDepositForm}
                        type='submit'
                        dataTest='multi-deposit-submit'
                    >
                        <I18n
                            langKey='account.top-up.submit-deposit-button'
                            defaultText='Proceed'
                        />
                    </PayButton>
                )}

                {showKycStatus === true ? <KycWarning isSignup={false} /> : null}
            </DepositTabWrapper>
        );
    }
);
